import { useEffect, useState } from 'react';
import { DatabaseBackup } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MaintenanceDTO } from '@lightbm/shared';
import { Spinner } from '@/components/ui/Spinner';
import { onMaintenance } from '@/lib/api-client';

const POLL_MS = 3000;

/** Asked outside the API's routes, which answer "maintenance" meanwhile. */
async function maintenanceStatus(): Promise<MaintenanceDTO | null> {
  try {
    const response = await fetch('/api/maintenance', { credentials: 'same-origin' });
    return ((await response.json()) as { data: MaintenanceDTO }).data;
  } catch {
    return null;
  }
}

/**
 * DAT-06: while the data is restored, every screen gives way to this one, which asks every few
 * seconds whether it is over, then opens the sign-in page (the restore signs everyone out).
 */
export function MaintenanceScreen() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<MaintenanceDTO | null>(null);

  useEffect(
    () =>
      onMaintenance(() =>
        setStatus((current) => current ?? { active: true, step: null, since: null }),
      ),
    [],
  );

  useEffect(() => {
    if (!status) return;
    let stopped = false;
    const poll = async () => {
      const next = await maintenanceStatus();
      if (stopped) return;
      if (next && !next.active) {
        window.location.assign('/sign-in?reason=restored');
        return;
      }
      if (next) setStatus(next);
      timer = window.setTimeout(() => void poll(), POLL_MS);
    };
    let timer = window.setTimeout(() => void poll(), POLL_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [status]);

  if (!status) return null;
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="maintenance-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ground p-6"
    >
      <div className="w-full max-w-md rounded-card border border-line bg-surface p-8 text-center shadow-card">
        <DatabaseBackup aria-hidden="true" className="mx-auto size-10 text-primary" />
        <h1 id="maintenance-title" className="mt-4 text-section font-semibold">
          {t('maintenance.title')}
        </h1>
        <p className="mt-2 text-ink-2">{t('maintenance.text')}</p>
        <p className="mt-6 flex items-center justify-center gap-2 font-medium text-primary">
          <Spinner className="size-5" />
          {status.step ? t(`maintenance.steps.${status.step}`) : t('maintenance.waiting')}
        </p>
      </div>
    </div>
  );
}
